import { CloudAccessType } from "@grabcad/company-server-shared/dist/types";
import {
    createGateway,
    getGatewayInstallerUrl,
    requestGatewayInstaller,
    removeGatewayInstaller,
    decommissionPrinters,
} from "../../../zeus-client/gatewaysApis";
import { TranslationFunction } from "../../../components/ApplicationProvider";
import { Notifier } from "../../../view/Notifier";

export enum InstallerContractStatus {
    Completed = "COMPLETED",
    Failed = "FAILED",
    Health = "HEALTHY",
    In_progress = "IN_PROGRESS",
    New = "NEW",
    Not_Requested = "NOT_REQUESTED",
    Unhealthy = "UNHEALTHY",
}

type CreateGatewayWithInstallerParams = {
    siteName: string;
    region: string;
    printerSerial?: string;
    cloudAccessType?: CloudAccessType;
};

type DownloadGatewayInstallerParams = {
    coreDeviceThingName: string;
    printerSerial?: string;
    t: TranslationFunction;
};

const downloadFile = (url: string): void => {
    const link = document.createElement("a");

    link.href = url;
    link.download = url.split("/").pop() || "gateway-installer";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const getInstallerStatusMessage = (
    status: InstallerContractStatus,
    t: TranslationFunction
): void => {
    const messageMap = {
        IN_PROGRESS: "gateways.installer_not_ready_msg",
        COMPLETED: "gateways.installer_start_msg",
        FAILED: "gateways.installer_creation_failed_msg",
        NOT_REQUESTED: "gateways.certificate_already_exists",
        HEALTHY: "gateways.installer_healthy",
        NEW: "gateways.installer_new",
        UNHEALTHY: "gateways.installer_unhealthy",
    } as const;

    const messageKey = messageMap[status] || "gateways.installer_creation_failed_msg";

    Notifier[status === InstallerContractStatus.Completed ? "success" : "fail"](t(messageKey));
};

export const createGatewayWithInstaller = async ({
    siteName,
    region,
    printerSerial,
    cloudAccessType,
}: CreateGatewayWithInstallerParams): Promise<string | undefined> => {
    const response = await createGateway(siteName, region, printerSerial, cloudAccessType);

    const coreDeviceThingName = response?.coreDeviceThingName;

    if (!coreDeviceThingName) {
        return undefined;
    }

    await requestGatewayInstaller(coreDeviceThingName, printerSerial);

    return coreDeviceThingName;
};

export const downloadGatewayInstaller = async ({
    coreDeviceThingName,
    printerSerial,
    t,
}: DownloadGatewayInstallerParams): Promise<void> => {
    try {
        const result = await getGatewayInstallerUrl(coreDeviceThingName, printerSerial);

        if (!result?.status) {
            return;
        }

        getInstallerStatusMessage(result.status as InstallerContractStatus, t);

        if (result.url) {
            downloadFile(result.url);
        }
    } catch {
        getInstallerStatusMessage(InstallerContractStatus.Not_Requested, t);
    }
};

/**
 * Removes the gateway installer.
 * printerSerial is optional and is used when the gateway
 * belongs to a specific client/company.
 */
export const deleteGatewayInstaller = async (
    coreDeviceThingName: string,
    printerSerial?: string
): Promise<void> => {
    if (!coreDeviceThingName) {
        return;
    }

    await removeGatewayInstaller(coreDeviceThingName, printerSerial);
};

/**
 * Decommissions a linked printer.
 * printerSerial is optional and is used when the request
 * is performed on behalf of a reseller/client.
 */
export const decommissionPrinter = async (
    clientDeviceThingName: string,
    printerSerial?: string
): Promise<void> => {
    if (!clientDeviceThingName) {
        return;
    }

    await decommissionPrinters(clientDeviceThingName, printerSerial);
};
